/**
 * KAN-468 — Learner profile fields service.
 *
 * Learner self-serve endpoints from the KAN-468 backend API contract v1
 * (2026-08-25):
 *   GET /custom-fields/me                    -> { fields, completion }
 *   PUT /custom-fields/me/values/{fieldId}   -> updated FieldWithValue
 *
 * Every response arrives in the standard {success, data} envelope; unwrap
 * defensively (res.data ?? res), matching the sibling ProfileService.
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ProfileCompletion, ProfileFieldWithValue } from './profile-fields.utils';

export interface MyProfileFields {
  fields: ProfileFieldWithValue[];
  completion: ProfileCompletion;
}

@Injectable({ providedIn: 'root' })
export class ProfileFieldsService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/custom-fields`;

  /** The signed-in learner's org profile fields with current values. */
  getMyProfileFields(): Observable<MyProfileFields> {
    return this.http.get<any>(`${this.apiUrl}/me`).pipe(
      map((res) => {
        const data = res?.data ?? res;
        return {
          fields: (data?.fields ?? []) as ProfileFieldWithValue[],
          completion: (data?.completion ?? {
            requestedCount: 0,
            completedCount: 0,
            isComplete: true,
          }) as ProfileCompletion,
        };
      })
    );
  }

  /** Write the caller's own value for one field; null clears it. */
  updateMyFieldValue(fieldId: string, value: unknown): Observable<ProfileFieldWithValue> {
    return this.http
      .put<any>(`${this.apiUrl}/me/values/${fieldId}`, { value })
      .pipe(map((res) => (res?.data ?? res) as ProfileFieldWithValue));
  }
}
